import React from 'react';
import { Plus, CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from '../ui/dialog';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

export const LoanFormDialog = ({ open, onOpenChange, form, setForm, onSubmit, editingId, onTriggerClick }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogTrigger asChild>
      <Button className="btn-press" data-testid="add-loan-btn" onClick={onTriggerClick}>
        <Plus className="mr-2 h-4 w-4" />
        New Loan
      </Button>
    </DialogTrigger>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-heading">{editingId ? 'Edit Loan' : 'Issue New Loan'}</DialogTitle>
        <DialogDescription>{editingId ? 'Update this loan record' : 'Create a new loan for a borrower'}</DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Borrower Name</Label>
            <Input
              value={form.borrower_name}
              onChange={(e) => setForm({ ...form, borrower_name: e.target.value })}
              placeholder="Full name"
              required
              data-testid="loan-borrower-name"
            />
          </div>
          <div className="space-y-2">
            <Label>Phone</Label>
            <Input
              value={form.borrower_phone}
              onChange={(e) => setForm({ ...form, borrower_phone: e.target.value })}
              placeholder="+256..."
              required
              data-testid="loan-phone"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Business Type</Label>
          <Input
            value={form.business_type}
            onChange={(e) => setForm({ ...form, business_type: e.target.value })}
            placeholder="e.g., Tailoring, Food Vending"
            required
            data-testid="loan-business"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Loan Amount (UGX)</Label>
            <Input
              type="number"
              value={form.loan_amount}
              onChange={(e) => setForm({ ...form, loan_amount: e.target.value })}
              placeholder="Amount"
              required
              data-testid="loan-amount"
            />
          </div>
          <div className="space-y-2">
            <Label>Interest Rate (%)</Label>
            <Input
              type="number"
              value={form.interest_rate}
              onChange={(e) => setForm({ ...form, interest_rate: e.target.value })}
              placeholder="10"
              required
              data-testid="loan-interest"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Issue Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className={cn("w-full justify-start text-left font-normal")}>
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {format(form.issue_date, 'PP')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={form.issue_date}
                  onSelect={(date) => setForm({ ...form, issue_date: date })}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="space-y-2">
            <Label>Duration (Months)</Label>
            <Input
              type="number"
              value={form.duration_months}
              onChange={(e) => setForm({ ...form, duration_months: e.target.value })}
              placeholder="6"
              required
              data-testid="loan-duration"
            />
          </div>
        </div>

        {/* Security & Savings */}
        <div className="grid grid-cols-2 gap-4 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/10">
          <div className="space-y-2">
            <Label>Security / Collateral</Label>
            <Input
              value={form.security}
              onChange={(e) => setForm({ ...form, security: e.target.value })}
              placeholder="e.g., Land title, motorbike"
              data-testid="loan-security"
            />
          </div>
          <div className="space-y-2">
            <Label>Savings (UGX)</Label>
            <Input
              type="number"
              value={form.savings}
              onChange={(e) => setForm({ ...form, savings: e.target.value })}
              placeholder="0"
              data-testid="loan-savings"
            />
          </div>
        </div>

        {/* Guarantor */}
        <div className="space-y-3 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/10">
          <p className="text-sm font-medium text-blue-700 dark:text-blue-400">Guarantor Information</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Guarantor Name</Label>
              <Input
                value={form.guarantor_name}
                onChange={(e) => setForm({ ...form, guarantor_name: e.target.value })}
                data-testid="loan-guarantor-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Relationship</Label>
              <Input
                value={form.guarantor_relationship}
                onChange={(e) => setForm({ ...form, guarantor_relationship: e.target.value })}
                placeholder="e.g., Husband, Brother"
                data-testid="loan-guarantor-relationship"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Guarantor Contact</Label>
              <Input
                value={form.guarantor_contact}
                onChange={(e) => setForm({ ...form, guarantor_contact: e.target.value })}
                placeholder="Phone number"
                data-testid="loan-guarantor-contact"
              />
            </div>
            <div className="space-y-2">
              <Label>Guarantor Address</Label>
              <Input
                value={form.guarantor_address}
                onChange={(e) => setForm({ ...form, guarantor_address: e.target.value })}
                data-testid="loan-guarantor-address"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" data-testid="loan-submit-btn">
            {editingId ? 'Update Loan' : 'Issue Loan'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);
